/**
 * WarehouseManagement — the warehouse list under Settings.
 *
 * Layout only: the data and the two actions on it live in `useWarehouseList`,
 * and the header, table and dialogs are their own components.
 */
import { AlertCircle, Search } from 'lucide-react';
import { useState } from 'react';

import WarehouseCreationModal from './components/WarehouseCreationModal';
import WarehouseDeactivateModal from './components/WarehouseDeactivateModal';
import WarehouseListHeader from './components/WarehouseListHeader';
import WarehouseTable from './components/WarehouseTable';
import { useWarehouseList } from './useWarehouseList';

const WarehouseManagement = () => {
  const list = useWarehouseList();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <WarehouseListHeader onCreate={() => setShowCreateModal(true)} />

      {list.error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle size={18} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-900 dark:text-red-100">{list.error}</p>
          </div>
        </div>
      )}

      <div className="relative">
        <Search size={16} className="absolute left-3 top-3 text-stone-400" />
        <input
          type="text"
          placeholder="Search warehouses..."
          value={list.searchTerm}
          onChange={(e) => list.setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg bg-white dark:bg-[#1a1f1c] text-bark-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500"
        />
      </div>

      <div className="bg-white dark:bg-[#1a1f1c] border rounded-lg overflow-hidden">
        <WarehouseTable
          rows={list.filtered}
          total={list.warehouses.length}
          loading={list.loading}
          onDelete={setDeleteConfirm}
        />
      </div>

      {deleteConfirm && (
        <WarehouseDeactivateModal
          deleting={list.deleting}
          onCancel={() => setDeleteConfirm(null)}
          onConfirm={() => {
            void list.remove(deleteConfirm).then((gone) => {
              if (gone) setDeleteConfirm(null);
            });
          }}
        />
      )}

      {showCreateModal && (
        <WarehouseCreationModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(warehouse) => {
            list.add(warehouse);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};

export default WarehouseManagement;
